import React, { useMemo, useState } from "react";
import { FaCheck } from "react-icons/fa";
import {
  filterApprovedTemplates,
  getTemplatePreview,
  templateKey,
} from "../../utils/templatePreview";

export function CampaignTemplatePreview({ selectedTemplate = null }) {
  const preview = useMemo(
    () => getTemplatePreview(selectedTemplate),
    [selectedTemplate],
  );

  return (
    <div className="biz-broadcast-preview">
      <h3>Template Preview</h3>
      {!preview ? (
        <p className="biz-broadcast-preview-empty">
          Select a template to preview the message.
        </p>
      ) : (
        <div className="biz-broadcast-wa-bubble">
          <div className="biz-broadcast-wa-meta">
            <strong>{preview.name}</strong>
            {preview.category ? <span>{preview.category}</span> : null}
          </div>
          {preview.headerFormat && preview.headerFormat !== "TEXT" ? (
            <div className="biz-broadcast-wa-media">
              [{preview.headerFormat} header]
            </div>
          ) : null}
          {preview.headerText ? (
            <p className="biz-broadcast-wa-header">{preview.headerText}</p>
          ) : null}
          <p className="biz-broadcast-wa-body">
            {preview.body || "No body text available for this template."}
          </p>
          {preview.footer ? (
            <p className="biz-broadcast-wa-footer">{preview.footer}</p>
          ) : null}
          {preview.buttons.length > 0 ? (
            <div className="biz-broadcast-wa-buttons">
              {preview.buttons.map((label) => (
                <span key={label}>{label}</span>
              ))}
            </div>
          ) : null}
        </div>
      )}
    </div>
  );
}

/**
 * Shared approved-template picker used by CSV / Broadcast / API / WhatsApp+SMS campaigns.
 */
const CampaignTemplatePicker = ({
  templates = [],
  selectedTemplate = null,
  onSelect,
  loading = false,
  showSearch = true,
  showPreview = true,
  title = "Choose a WhatsApp template",
  subtitle = "Only approved templates are shown for this campaign.",
  className = "",
}) => {
  const [search, setSearch] = useState("");

  const filteredTemplates = useMemo(() => {
    const approved = filterApprovedTemplates(templates);
    const q = search.trim().toLowerCase();
    if (!q) return approved;
    return approved.filter((tpl) => {
      const hay =
        `${tpl.name || ""} ${tpl.category || ""} ${tpl.language || ""}`.toLowerCase();
      return hay.includes(q);
    });
  }, [templates, search]);

  return (
    <div className={`biz-broadcast-templates ${className}`.trim()}>
      <div className="biz-broadcast-templates-head">
        <div>
          <h2>{title}</h2>
          <p className="biz-broadcast-templates-sub">{subtitle}</p>
        </div>
        {showSearch ? (
          <input
            type="search"
            className="biz-broadcast-template-search"
            placeholder="Search by name or category…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            aria-label="Search templates"
          />
        ) : null}
      </div>

      {loading ? (
        <p className="biz-panel-hint">Loading templates…</p>
      ) : filteredTemplates.length === 0 ? (
        <p className="biz-table-empty">No approved templates found.</p>
      ) : (
        <div
          className={`biz-broadcast-template-layout${
            showPreview ? "" : " biz-broadcast-template-layout--grid-only"
          }`}
        >
          <div className="biz-broadcast-template-grid">
            {filteredTemplates.map((tpl) => {
              const selected =
                Boolean(selectedTemplate) &&
                templateKey(selectedTemplate) === templateKey(tpl);
              return (
                <button
                  key={templateKey(tpl)}
                  type="button"
                  className={`biz-broadcast-template-card${
                    selected ? " is-selected" : ""
                  }`}
                  aria-pressed={selected}
                  onClick={() => onSelect?.(tpl)}
                >
                  <div className="biz-broadcast-template-card-top">
                    <strong title={tpl.name}>{tpl.name}</strong>
                    {selected ? (
                      <span
                        className="biz-broadcast-template-check"
                        aria-hidden="true"
                      >
                        <FaCheck />
                      </span>
                    ) : null}
                  </div>
                  <span className="biz-broadcast-template-meta">
                    {tpl.category || "—"}
                    {tpl.language ? ` · ${tpl.language}` : ""}
                  </span>
                </button>
              );
            })}
          </div>

          {showPreview ? (
            <CampaignTemplatePreview selectedTemplate={selectedTemplate} />
          ) : null}
        </div>
      )}
    </div>
  );
};

export default CampaignTemplatePicker;
